import React, { useEffect, useState } from "react";
import { Button, Flex, Grid, GridItem, Icon, Text } from "@chakra-ui/react";
import {
  backgroundLight,
  newBorder,
  primaryAlt,
  textPrimary,
  textSecondary,
  white,
} from "../../../../../utils/colors";
import { useTranslation } from "react-i18next";
import StatusBadgeNew from "../../../../../components/badges/StatusBadgeNew";
import PatientPortalOnePlan from "./PatientPortalOnePlan";
import { FaArrowLeftLong } from "react-icons/fa6";
import dayjs from "dayjs";

const PatientPortalMultiplePlans = ({
  treatmentPlans,
  clinicName,
  token,
  patientPortalPublicDetails,
}) => {
  const { t } = useTranslation();

  const [selectedPlan, setSelectedPlan] = useState(null);

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    localStorage.setItem("selectedPlanId", plan._id);
  };

  // USE EFFECT
  useEffect(() => {
    const storedId = localStorage.getItem("selectedPlanId");

    if (storedId && treatmentPlans.length > 0) {
      const matchedPlan = treatmentPlans.find((p) => p._id === storedId);
      if (matchedPlan) {
        setSelectedPlan(matchedPlan);
      } else {
        localStorage.removeItem("selectedPlanId");
      }
    } else if (treatmentPlans.length === 1) {
      setSelectedPlan(treatmentPlans[0]);
      localStorage.setItem("selectedPlanId", treatmentPlans[0]._id);
    }
  }, [treatmentPlans]);

  // LOG
  // console.log("treatmentPlans: ", treatmentPlans);
  // console.log("selectedPlan: ", selectedPlan);

  return (
    <>
      {selectedPlan ? (
        <>
          <Flex
            w="100%"
            align="center"
            justify="center"
            direction="column"
            maxW={["100%", "1200px"]}
            m="0 auto"
            position="relative"
          >
            <Flex
              justify={["flex-start", "center"]}
              position={["relative", "absolute"]}
              left={["initial", "2rem"]}
              top={["initial", "14px"]}
              w={["100%", "auto"]}
              p={["0 1.5rem 2rem", "initial"]}
            >
              <Button
                onClick={() => {
                  setSelectedPlan(null);
                  localStorage.removeItem("selectedPlanId");
                }}
                variant="link"
                fontWeight="500"
                _hover={{
                  textDecoration: "none",
                  color: primaryAlt,
                }}
              >
                <Icon as={FaArrowLeftLong} fontSize="sm" mr={2} />
                {t("patient.portalBackToPlans")}
              </Button>
            </Flex>
            <Flex align="center" justify="center" direction="column">
              <Flex align="center">
                <Text
                  fontSize={["1.25rem", "2rem"]}
                  color={textPrimary}
                  fontWeight="500"
                >
                  {t("treatmentPlan.treatmentPlan")} -
                </Text>
                <Text
                  fontSize={["1.25rem", "2rem"]}
                  color={primaryAlt}
                  fontWeight="500"
                  textAlign="center"
                  ml={2}
                >
                  {selectedPlan.treatmentPlanID}
                </Text>
              </Flex>
              <Flex
                align="center"
                justify="center"
                m={["0.25rem 0 0", "0.5rem 0 0"]}
              >
                <Text
                  fontSize="md"
                  color={textSecondary}
                  fontWeight="500"
                  mr={1.5}
                >
                  {t("treatmentPlan.createdAt")}:
                </Text>
                <Text fontSize="md" color={textSecondary} fontWeight="500">
                  {dayjs(selectedPlan.createdAt).format("YYYY-MM-D")}
                </Text>
              </Flex>
            </Flex>
          </Flex>
          {/* Show single plan */}
          <PatientPortalOnePlan
            treatmentPlans={[selectedPlan]} // still pass as array if your component expects it
            clinicName={clinicName}
            token={token}
            patientPortalPublicDetails={patientPortalPublicDetails}
          />
        </>
      ) : (
        <>
          <Flex
            w="100%"
            align="center"
            justify="center"
            direction="column"
            p={["0 1.5rem", "initial"]}
          >
            {/* <Text
              fontSize="1.25rem"
              color={textPrimary}
              fontWeight="500"
              textAlign="center"
            >
              {clinicName && clinicName}
            </Text> */}
            <Text
              fontSize={["1.5rem", "2rem"]}
              color={textPrimary}
              fontWeight="500"
              m="0.25rem 0 0.5rem"
              textAlign="center"
            >
              {t("patient.multiplePlansTitle")}
            </Text>

            <Text
              fontSize="md"
              color={textSecondary}
              fontWeight="500"
              textAlign="center"
              maxW={["100%", "800px"]}
              p={["0 0rem", "0 3rem"]}
              m="0 auto"
              lineHeight="1.5"
            >
              {t("patient.portalDescription")}
            </Text>
          </Flex>

          <Flex w="100%" p="2rem 0 6rem">
            <Flex
              w="100%"
              maxW={["100%", "1000px"]}
              m="0 auto"
              bg={backgroundLight}
              p={["1.5rem 1.5rem", "2rem"]}
              borderRadius={["0rem", "2rem"]}
            >
              <Grid
                templateColumns={["repeat(1, 1fr)", "repeat(2, 1fr)"]}
                gap={6}
                w="100%"
              >
                {treatmentPlans &&
                  treatmentPlans.map((plan, index) => (
                    <GridItem colSpan={1} key={index}>
                      <Flex
                        bg={white}
                        key={index}
                        w="100%"
                        direction="column"
                        p={["1.5rem 1.5rem 1rem", "1.5rem 1.5rem 1rem"]}
                        borderRadius="1rem"
                        position="relative"
                        transition="all 0.2s"
                        _hover={{
                          boxShadow: "0 0 10px rgba(0, 0, 0, 0.03)",
                        }}
                      >
                        <Flex
                          align="center"
                          justify="space-between"
                          fontSize={["1rem", "1.125rem"]}
                          color={textPrimary}
                          fontWeight="500"
                        >
                          <Flex align="baseline" direction={["column", "row"]}>
                            <Text mr={1.5}>
                              {t("treatmentPlan.treatmentPlan")}{" "}
                            </Text>
                            {/* <Text color="#ddd" mx={2} fontWeight="300">
                              |
                            </Text> */}

                            <Text
                              fontSize={["1rem", "1.125rem"]}
                              color={textPrimary}
                            >
                              {plan.treatmentPlanID}
                            </Text>
                          </Flex>

                          <Flex align="center" justify="flex-end">
                            <StatusBadgeNew
                              status={plan.status}
                              options="treatmentPublic"
                              p={["0.5rem 0.5rem", "0.5rem 1rem"]}
                              fontSize="0.8rem"
                            />
                            {/* <Text
                              color={textSecondary}
                              fontSize="md"
                              fontWeight="500"
                            >
                              {dayjs(plan.createdAt).format("YYYY-MM-D")}
                            </Text> */}
                          </Flex>
                        </Flex>

                        <Flex
                          w="100%"
                          direction="column"
                          m="1.5rem 0"
                          fontSize="sm"
                          gap="0.6rem"
                        >
                          {/* PATIENT NAME */}
                          <Flex align="center">
                            <Text color={textSecondary} lineHeight="1">
                              {t("patient.patient")}:
                            </Text>
                            <Text fontWeight="500" ml={1} lineHeight="1">
                              {plan.patient && plan.patient.firstName}{" "}
                              {plan.patient && plan.patient.lastName}
                            </Text>
                          </Flex>

                          {/* PLAN CREATED AT */}
                          <Flex align="center">
                            <Text color={textSecondary} lineHeight="1">
                              {t("treatmentPlan.createdAtAlt")}:
                            </Text>
                            <Text fontWeight="500" ml={1} lineHeight="1">
                              {dayjs(plan.createdAt).format("YYYY-MM-D")}
                            </Text>
                          </Flex>

                          {/* PLAN PRICE */}
                          <Flex align="center">
                            <Text color={textSecondary} lineHeight="1">
                              {t("treatmentPlan.treatmentPlanPrice")}:
                            </Text>
                            <Text fontWeight="500" ml={1} lineHeight="1">
                              {plan.totalPrice} €
                            </Text>
                          </Flex>

                          {/* PATIENT PHONE */}

                          {/* COORDINATOR */}
                          {plan.coordinator &&
                            plan.coordinator.firstName &&
                            plan.coordinator.lastName && (
                              <Flex align="center">
                                <Text color={textSecondary} lineHeight="1">
                                  {t("patient.planCoordinatorPublicLabel")}:
                                </Text>
                                <Text fontWeight="500" ml={1} lineHeight="1">
                                  {plan.coordinator.firstName[0] + "."}{" "}
                                  {plan.coordinator.lastName}
                                </Text>
                              </Flex>
                            )}
                        </Flex>

                        {/* PRICE AND STATUS */}

                        {/* VIEW BUTTON */}
                        <Flex
                          w="100%"
                          borderTop={`1px solid ${newBorder}`}
                          align="center"
                          justify="center"
                          p="1rem 0 0"
                          // m="1.5rem 0 0"
                        >
                          <Button
                            bg={textPrimary}
                            color={white}
                            fontWeight="500"
                            size="md"
                            fontSize="sm"
                            px="1.5rem"
                            borderRadius="10rem"
                            _hover={{
                              bg: primaryAlt,
                            }}
                            onClick={() => handleSelectPlan(plan)}
                          >
                            {t("patient.portalViewPlan")}
                          </Button>
                        </Flex>
                      </Flex>
                    </GridItem>
                  ))}
              </Grid>
            </Flex>
          </Flex>
        </>
      )}
    </>
  );
};

export default PatientPortalMultiplePlans;
